import { useState } from 'react';

export default function SmartTechnology() {
  const [activeTech, setActiveTech] = useState(0);

  const technologies = [
    {
      title: 'Smart Lighting System',
      description: 'Intelligent LED lighting with automated daylight adjustment, scene presets, and energy monitoring for optimal workspace illumination',
      features: ['Automated brightness control', 'Color temperature adjustment', 'Motion-activated zones', 'Energy usage analytics'],
      icon: 'ri-lightbulb-line',
      image: '/images/ai-modern-intelligent-led-lighting-system-i-8cba76.webp'
    },
    {
      title: 'Climate Control',
      description: 'Advanced HVAC system with precision temperature and humidity control, air quality monitoring, and energy-efficient operation',
      features: ['Multi-zone temperature control', 'Air quality sensors', 'Smart ventilation', 'Energy optimisation'],
      icon: 'ri-temp-cold-line',
      image: '/images/ai-advanced-climate-control-system-in-moder-ec91cc.webp'
    },
    {
      title: 'Electrical Infrastructure',
      description: 'Commercial-grade electrical system with smart power management, multiple outlets, and support for high-power equipment',
      features: ['Smart power distribution', 'Surge protection', 'USB-C fast charging', 'Backup power ready'],
      icon: 'ri-plug-line',
      image: '/images/ai-modern-electrical-infrastructure-with-sm-38fe83.webp'
    },
    {
      title: 'Intelligent Management',
      description: 'Centralized control system accessible via mobile app for monitoring and managing all cabin systems remotely',
      features: ['Mobile app control', 'Usage analytics', 'Maintenance alerts', 'Security monitoring'],
      icon: 'ri-smartphone-line',
      image: '/images/ai-smart-home-control-system-with-mobile-ap-6840a2.webp'
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0A0A0A] to-transparent w-full">
      <div className="max-w-7xl mx-auto w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-thin mb-4 sm:mb-6 px-4">
            Smart <span className="text-gray-300">Technology</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Advanced systems for optimal workspace performance
          </p>
        </div>

        {/* Technology Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 w-full">
          {technologies.map((tech, index) => (
            <div 
              key={index}
              onMouseEnter={() => setActiveTech(index)}
              className={`group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 w-full ${
                activeTech === index
                  ? 'border-gray-400 shadow-[0_0_40px_rgba(148,163,184,0.3)]'
                  : 'border-white/10 hover:border-gray-500/30'
              }`}
            >
              {/* Image */}
              <div className="relative w-full h-48 sm:h-56 md:h-64 overflow-hidden">
                <img 
                  src={tech.image}
                  alt={tech.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center transition-all flex-shrink-0 ${
                    activeTech === index ? 'bg-gray-500/20' : 'bg-white/10'
                  }`}>
                    <i className={`${tech.icon} text-xl sm:text-2xl ${activeTech === index ? 'text-gray-300' : 'text-gray-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl sm:text-2xl font-medium text-white mb-2 break-words">{tech.title}</h3>
                    <p className="text-sm sm:text-base text-gray-400 break-words">{tech.description}</p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 sm:space-y-4 w-full">
                  {tech.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 sm:gap-3 bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4 w-full">
                      <i className="ri-checkbox-circle-fill text-gray-400 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                      <span className="text-xs sm:text-sm text-gray-300 break-words">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
